/**
 * CreateProjectDialog — mint an empty project from a name (+ optional
 * brief), no netlist required.
 *
 * Editorial treatment is intentional and forms the design vocabulary
 * we want to extend across the rest of the app:
 *   - Trace-cyan edge-glow on the dialog paper (instrument panel feel)
 *   - Gradient header wash + bottom hairline (powering up the rail)
 *   - Mono ALL-CAPS kickers (eyebrow, workspace label, char counter)
 *     paired with sans-serif body and form text
 *   - Stagger-rise reveal on mount
 *   - Glow on the primary commit button
 *
 * Mono is used only for kickers / labels — body text and form fields
 * stay in the default sans, so the type system reads as a single
 * coherent vocabulary rather than mixed fonts.
 */
import type { Organisation } from "@models/organisation";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardRounded";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import CloseIcon from "@mui/icons-material/CloseRounded";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { useProjectStore } from "@stores/projectStore";
import { useEffect, useState } from "react";
import { useCreateProject } from "../../hooks/useProjects";

const NAME_MAX = 120;

interface CreateProjectDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fires after a successful create. Receives the new project's UUID so
   * callers can surface a toast, route into the project, etc.
   */
  onCreated?: (projectId: string) => void;
  /** Active org context when the dialog opens. null → personal. */
  activeOrg: Organisation | null;
}

export function CreateProjectDialog({
  open,
  onClose,
  onCreated,
  activeOrg,
}: CreateProjectDialogProps) {
  const create = useCreateProject();
  const setActiveProject = useProjectStore((s) => s.setActiveProject);

  const [name, setName] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  // Reset every time the dialog is re-opened so a previous attempt
  // doesn't bleed across sessions.
  // biome-ignore lint/correctness/useExhaustiveDependencies: create is a stable mutation reference — listing it would loop
  useEffect(() => {
    if (open) {
      setName("");
      setServerError(null);
      create.reset();
    }
  }, [open]);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !create.isPending;

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!canSubmit) return;

    create.mutate(
      {
        name: trimmedName,
        organisationId: activeOrg?.id ?? null,
      },
      {
        onSuccess: (project) => {
          setActiveProject(project.id);
          onCreated?.(project.id);
          onClose();
        },
        onError: (err) => {
          const msg =
            err instanceof Error ? err.message : "Could not create project";
          setServerError(
            msg.startsWith("[object")
              ? "Could not create project — try a different name."
              : msg,
          );
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={create.isPending ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: {
          sx: {
            width: "100%",
            maxWidth: 520,
            m: { xs: 1, sm: 4 },
            overflow: "hidden",
            // Hairline edge-glow that fades in on mount — frames the
            // dialog as a discrete "instrument panel" rather than a
            // floating sheet.
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              borderRadius: "inherit",
              boxShadow: (t) =>
                `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.18)}, 0 0 32px -12px ${alpha(t.palette.primary.main, 0.4)}`,
            },
          },
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          // Stagger reveal — each child slides into place 80 ms after
          // the previous, mirroring the WelcomeScreen's cf-welcome-rise
          // pattern so the dialog reads as a continuation of the same
          // surface.
          "& > .cf-stage": {
            opacity: 0,
            animation: "cf-create-rise 420ms ease-out forwards",
          },
          "& > .cf-stage:nth-of-type(1)": { animationDelay: "40ms" },
          "& > .cf-stage:nth-of-type(2)": { animationDelay: "120ms" },
          "& > .cf-stage:nth-of-type(3)": { animationDelay: "200ms" },
          "@keyframes cf-create-rise": {
            from: { opacity: 0, transform: "translateY(6px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {/* ── Header ──────────────────────────────────────────────── */}
        <Box
          className="cf-stage"
          sx={{
            position: "relative",
            px: 3,
            pt: 2.5,
            pb: 2,
            // Soft trace-cyan wash behind the header — gives the top
            // of the dialog the look of a freshly-energised rail.
            background: (t) =>
              `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.06)} 0%, transparent 100%)`,
            borderBottom: (t) =>
              `1px solid ${alpha(t.palette.primary.main, 0.12)}`,
          }}
        >
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            disabled={create.isPending}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Eyebrow — pulsing trace dot + mono ALL-CAPS kicker.
              Matches the WelcomeScreen vocabulary
              (READY / TRACING / EMPTY); INIT is the create-mode
              counterpart. */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "primary.main",
                boxShadow: (t) =>
                  `0 0 0 2px ${alpha(t.palette.primary.main, 0.18)}`,
                animation: "cf-init-pulse 1.6s ease-in-out infinite",
                "@keyframes cf-init-pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.4 },
                },
              }}
            />
            <Typography
              sx={{
                fontFamily: (t) => t.fonts.mono,
                fontSize: "0.7rem",
                fontWeight: 500,
                color: "text.secondary",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
              }}
            >
              Init · New project
            </Typography>
          </Box>

          <Typography
            component="h2"
            sx={{
              color: "text.primary",
              fontSize: { xs: "1.375rem", sm: "1.5rem" },
              fontWeight: 700,
              letterSpacing: "-0.018em",
              lineHeight: 1.2,
            }}
          >
            New project
            <Box
              component="span"
              sx={{
                color: "primary.light",
                textShadow: (t) =>
                  `0 0 14px ${alpha(t.palette.primary.light, 0.55)}`,
              }}
            >
              .
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "0.875rem",
              lineHeight: 1.55,
              mt: 0.75,
              maxWidth: 440,
            }}
          >
            Pick a name. You can add boards, upload files, and write a
            brief from the project's overview after this.
          </Typography>
        </Box>

        {/* ── Body ───────────────────────────────────────────────── */}
        <DialogContent
          className="cf-stage"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 2.25,
            pb: 1.5,
          }}
        >
          {/* Workspace strip — slim hairline rail with a mono kicker
              and the org/personal name. The org context matters but
              shouldn't dominate the hierarchy. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                borderRadius: 0.75,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                color: "primary.light",
              }}
            >
              {activeOrg ? (
                <BusinessIcon sx={{ fontSize: 14 }} />
              ) : (
                <PersonIcon sx={{ fontSize: 14 }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: (t) => t.fonts.mono,
                  fontSize: "0.65rem",
                  fontWeight: 500,
                  color: "text.secondary",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                Workspace
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color: "text.primary",
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {activeOrg ? activeOrg.name : "Personal"}
              </Typography>
            </Box>
          </Box>

          <TextField
            autoFocus
            size="small"
            label="Project name"
            placeholder="e.g., Gateway v3, Sensor Hub"
            fullWidth
            value={name}
            onChange={(e) => {
              setName(e.target.value.slice(0, NAME_MAX));
              if (serverError) setServerError(null);
            }}
            inputProps={{ maxLength: NAME_MAX }}
            helperText={
              serverError && create.isError
                ? null
                : "A short, recognisable name. You can rename later."
            }
            disabled={create.isPending}
          />

          {serverError && (
            <Alert severity="error" onClose={() => setServerError(null)}>
              {serverError}
            </Alert>
          )}
        </DialogContent>

        {/* ── Actions ─────────────────────────────────────────────── */}
        <DialogActions
          className="cf-stage"
          sx={{
            px: 3,
            pb: 2.25,
            pt: 0.5,
            gap: 1,
            // Hairline divider above the actions — separates the form
            // inputs from the commit gesture without the heavy
            // DialogContent bottom border.
            borderTop: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
            mt: 0.5,
          }}
        >
          <Button
            variant="text"
            onClick={onClose}
            size="small"
            disabled={create.isPending}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            disabled={!canSubmit}
            endIcon={
              create.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <ArrowForwardIcon fontSize="small" />
              )
            }
            sx={{
              px: 2,
              fontWeight: 600,
              letterSpacing: "-0.005em",
              textTransform: "none",
              boxShadow: (t) =>
                `0 0 0 1px ${alpha(t.palette.primary.main, 0.4)}, 0 4px 18px -8px ${alpha(t.palette.primary.main, 0.6)}`,
              "&:hover": {
                boxShadow: (t) =>
                  `0 0 0 1px ${alpha(t.palette.primary.main, 0.6)}, 0 6px 22px -8px ${alpha(t.palette.primary.main, 0.8)}`,
              },
            }}
          >
            {create.isPending ? "Creating…" : "Create project"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
